import FormInput from "./FormInput";
import FormTextarea from "./FormTextarea";
import FormSelect from "./FormSelect";
import OfferedItemsInput from "./OfferedItemsInput";

import {
    industryOptions,
    toneOptions,
    goalOptions,
    ctaOptions,
    brandStyleOptions,
    pricePositioningOptions,
    getRecommendedPages
} from "../../data/websiteOptions";

export default function BusinessDetailsStep({ form, updateField, onAutofill, autofilling }) {
    return (
        <div>
            <h2 className="wg-section-title">Business Details</h2>
            <p className="wg-section-subtitle">
                Enter your core business information first. Then use AI Autofill to enrich the rest.
            </p>

            <div className="wg-action-bar" style={{ marginBottom: 20 }}>
                <button
                    type="button"
                    className="wg-btn primary"
                    onClick={onAutofill}
                    disabled={autofilling}
                >
                    {autofilling ? "Autofilling..." : "Autofill with AI"}
                </button>
            </div>

            <div className="wg-form-grid">
                <FormInput
                    label="Business Name"
                    value={form.businessName}
                    onChange={(v) => updateField("businessName", v)}
                />

                <FormSelect
                    label="Industry"
                    value={form.industry}
                    options={industryOptions}
                    onChange={(v) => updateField("industry", v)}
                />

                <FormInput
                    label="Sub Industry"
                    value={form.subIndustry}
                    onChange={(v) => updateField("subIndustry", v)}
                />

                <div className="wg-subsection">
                    <div className="wg-subsection-header">
                        <strong>Recommended Pages</strong>
                    </div>

                    <div className="wg-chip-row">
                        {getRecommendedPages(form.industry, form.subIndustry).map((page) => (
                            <button
                                key={page}
                                type="button"
                                className={`wg-chip ${(form.pages || []).includes(page) ? "active" : ""}`}
                                onClick={() => {
                                    const exists = (form.pages || []).includes(page);

                                    updateField(
                                        "pages",
                                        exists
                                            ? form.pages.filter((p) => p !== page)
                                            : [...form.pages, page]
                                    );
                                }}
                            >
                                {page}
                            </button>
                        ))}
                    </div>
                </div>

                <FormSelect
                    label="Tone"
                    value={form.tone}
                    options={toneOptions}
                    onChange={(v) => updateField("tone", v)}
                />

                <FormInput
                    label="Primary Audience"
                    value={form.primaryAudience}
                    onChange={(v) => updateField("primaryAudience", v)}
                />

                <FormInput
                    label="Secondary Audience"
                    value={form.secondaryAudience}
                    onChange={(v) => updateField("secondaryAudience", v)}
                />

                <FormInput
                    label="Location"
                    value={form.location}
                    onChange={(v) => updateField("location", v)}
                />

                <FormInput
                    label="Service Areas"
                    value={form.serviceAreas}
                    onChange={(v) => updateField("serviceAreas", v)}
                />

                <FormSelect
                    label="Goal of Website"
                    value={form.goalOfWebsite}
                    options={goalOptions}
                    onChange={(v) => updateField("goalOfWebsite", v)}
                />

                <FormSelect
                    label="CTA Type"
                    value={form.callToActionType}
                    options={ctaOptions}
                    onChange={(v) => updateField("callToActionType", v)}
                />

                <FormSelect
                    label="Brand Style"
                    value={form.brandStyle}
                    options={brandStyleOptions}
                    onChange={(v) => updateField("brandStyle", v)}
                />

                <FormSelect
                    label="Layout Style"
                    value={form.desiredLayoutStyle}
                    options={[
                        { label: "Auto (AI Decide)", value: "" },
                        { label: "Search First (Marketplace)", value: "search-first" },
                        { label: "Product First (Ecommerce)", value: "product-first" },
                        { label: "Conversion Focused", value: "conversion-first" },
                        { label: "Program / Info First", value: "program-first" }
                    ]}
                    onChange={(v) => updateField("desiredLayoutStyle", v)}
                />

                <FormSelect
                    label="Visual Tone"
                    value={form.visualTone}
                    options={[
                        { label: "Auto", value: "" },
                        { label: "Clean & Professional", value: "clean" },
                        { label: "Premium & Luxury", value: "premium" },
                        { label: "Modern & Tech", value: "modern" },
                        { label: "Friendly & Casual", value: "friendly" }
                    ]}
                    onChange={(v) => updateField("visualTone", v)}
                />

                <FormSelect
                    label="Content Density"
                    value={form.preferredSectionDensity}
                    options={[
                        { label: "Auto", value: "" },
                        { label: "Minimal", value: "minimal" },
                        { label: "Balanced", value: "balanced" },
                        { label: "Detailed", value: "detailed" }
                    ]}
                    onChange={(v) => updateField("preferredSectionDensity", v)}
                />

                <FormSelect
                    label="Price Positioning"
                    value={form.pricePositioning}
                    options={pricePositioningOptions}
                    onChange={(v) => updateField("pricePositioning", v)}
                />

                <FormTextarea
                    label="Description"
                    value={form.description}
                    onChange={(v) => updateField("description", v)}
                />

                <FormTextarea
                    label="Core Services"
                    value={form.coreServices}
                    onChange={(v) => updateField("coreServices", v)}
                />

                <FormTextarea
                    label="Project Types / Products"
                    value={form.projectTypes}
                    onChange={(v) => updateField("projectTypes", v)}
                />

                <FormTextarea
                    label="Unique Selling Points"
                    value={form.uniqueSellingPoints}
                    onChange={(v) => updateField("uniqueSellingPoints", v)}
                />

                <FormTextarea
                    label="Trust Signals"
                    value={form.trustSignals}
                    onChange={(v) => updateField("trustSignals", v)}
                />

                <FormInput
                    label="Years in Business"
                    value={form.yearsInBusiness}
                    onChange={(v) => updateField("yearsInBusiness", v)}
                />

                <FormTextarea
                    label="Must Include Words"
                    value={form.mustIncludeWords}
                    onChange={(v) => updateField("mustIncludeWords", v)}
                />

                <FormTextarea
                    label="Avoid Words"
                    value={form.avoidWords}
                    onChange={(v) => updateField("avoidWords", v)}
                />

                <FormInput
                    label="Competitor Website URL"
                    value={form.competitorWebsiteUrl}
                    onChange={(v) => updateField("competitorWebsiteUrl", v)}
                />

                <FormTextarea
                    label="Competitor Style Notes"
                    value={form.competitorStyleNotes}
                    onChange={(v) => updateField("competitorStyleNotes", v)}
                />

                <OfferedItemsInput
                    items={form.offeredItems}
                    onChange={(items) => updateField("offeredItems", items)}
                />
            </div>
        </div>
    );
}